import React from 'react';
import { Todo, BucketTheme } from '../types';
import { CheckCircle2, Circle, Clock, Pause, Play, Pencil, X, Calendar, CalendarClock, CalendarDays, ExternalLink, Hourglass, ListTodo } from 'lucide-react';
import { useLanguage } from '../i18n';

interface TaskItemProps {
  todo: Todo;
  bucketId: string;
  bucketName?: string;
  bucketTheme: BucketTheme;
  isExternal?: boolean;
  sourceBucketName?: string;
  isClosed?: boolean;
  isActive: boolean;
  currentTick: number;
  onToggle: () => void;
  onToggleTimer: () => void;
  onEdit: () => void;
  onDelete: () => void;
  showBucketName?: boolean;
}

const THEME_COLORS: Record<BucketTheme, { bg: string; fill: string; accent: string; text: string }> = {
  [BucketTheme.Blue]: { bg: 'bg-blue-950/30', fill: 'from-blue-500 to-cyan-400', accent: 'text-blue-400', text: 'text-blue-50' },
  [BucketTheme.Purple]: { bg: 'bg-purple-950/30', fill: 'from-purple-500 to-fuchsia-400', accent: 'text-purple-400', text: 'text-purple-50' },
  [BucketTheme.Green]: { bg: 'bg-emerald-950/30', fill: 'from-emerald-500 to-green-400', accent: 'text-emerald-400', text: 'text-emerald-50' },
  [BucketTheme.Orange]: { bg: 'bg-orange-950/30', fill: 'from-orange-500 to-amber-400', accent: 'text-orange-400', text: 'text-orange-50' },
  [BucketTheme.Pink]: { bg: 'bg-pink-950/30', fill: 'from-pink-500 to-rose-400', accent: 'text-pink-400', text: 'text-pink-50' },
  [BucketTheme.Red]: { bg: 'bg-red-950/30', fill: 'from-red-600 to-orange-500', accent: 'text-red-400', text: 'text-red-50' },
  [BucketTheme.Teal]: { bg: 'bg-teal-950/30', fill: 'from-teal-500 to-emerald-400', accent: 'text-teal-400', text: 'text-teal-50' },
  [BucketTheme.Yellow]: { bg: 'bg-yellow-950/30', fill: 'from-yellow-400 to-orange-400', accent: 'text-yellow-400', text: 'text-yellow-50' },
  [BucketTheme.Indigo]: { bg: 'bg-indigo-950/30', fill: 'from-indigo-500 to-violet-400', accent: 'text-indigo-400', text: 'text-indigo-50' },
  [BucketTheme.Cyan]: { bg: 'bg-cyan-950/30', fill: 'from-cyan-500 to-sky-400', accent: 'text-cyan-400', text: 'text-cyan-50' },
  [BucketTheme.Lime]: { bg: 'bg-lime-950/30', fill: 'from-lime-500 to-green-400', accent: 'text-lime-400', text: 'text-lime-50' },
  [BucketTheme.Rose]: { bg: 'bg-rose-950/30', fill: 'from-rose-500 to-pink-400', accent: 'text-rose-400', text: 'text-rose-50' },
};

const formatDuration = (ms: number) => {
    if (!ms) return null;
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)));

    const p = (n: number) => n.toString().padStart(2, '0');
    
    if (hours > 0) return `${hours}:${p(minutes)}:${p(seconds)}`;
    return `${p(minutes)}:${p(seconds)}`;
};

export const TaskItem: React.FC<TaskItemProps> = ({ 
    todo, bucketTheme, isExternal, sourceBucketName, bucketName,
    isClosed, isActive, currentTick, 
    onToggle, onToggleTimer, onEdit, onDelete, showBucketName 
}) => {
    const { t } = useLanguage();
    const colors = THEME_COLORS[bucketTheme];

    const isSnoozed = todo.snoozedUntil && todo.snoozedUntil > Date.now();
    const hasTargetCount = todo.targetCount && todo.targetCount > 1;
    const currentCount = todo.currentCount || 0;
    const isPartiallyDone = hasTargetCount && currentCount > 0 && currentCount < (todo.targetCount || 1);
                
    // Calculate display time
    const accumulatedTime = todo.timeSpent || 0;
    const currentSessionTime = isActive ? (currentTick - (todo.activeSessionStart || currentTick)) : 0;
    const totalDisplayTime = accumulatedTime + currentSessionTime;
    const timeString = formatDuration(totalDisplayTime);

    const getSnoozeLabel = (timestamp: number) => {
      const date = new Date(timestamp);
      const day = date.getDate();
      const month = t.monthNames[date.getMonth()];
      return `${t.next}: ${day}. ${month}`;
    };

    const getRecurrenceLabel = (recurrence: string) => {
        switch(recurrence) {
            case 'daily': return t.daily;
            case 'weekly': return t.weekly;
            case 'monthly': return t.monthly;
            default: return '';
        }
    }

    const getScheduledDateLabel = (timestamp: number) => {
        const date = new Date(timestamp);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const tomorrow = today + 86400000;
        const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

        if (inputDate === today) return t.today;
        if (inputDate === tomorrow) return t.tomorrow;
        return `${date.getDate()}. ${t.monthNames[date.getMonth()]}`;
    };

    return (
        <div 
        className={`group/item flex gap-3 p-3 rounded-xl transition-all border ${
            isSnoozed 
            ? 'bg-black/20 border-white/5 opacity-50' 
            : isActive
                ? 'bg-indigo-900/60 border-indigo-500/40 shadow-lg shadow-indigo-500/20'
                : isExternal 
                    ? 'bg-slate-800/60 border-slate-700/60 hover:bg-slate-800/70'
                    : 'bg-black/40 hover:bg-black/50 backdrop-blur-sm border-white/10'
        }`}
        >
        <button 
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            disabled={!!isSnoozed || !!isClosed}
            className={`flex-shrink-0 mt-0.5 transition-all ${
                todo.completed ? colors.accent : 
                isSnoozed || isClosed ? 'text-white/10 cursor-not-allowed' : 'text-white/30 hover:text-white/60'
            }`}
        >
            {todo.completed ? (
            <CheckCircle2 size={20} className="fill-current bg-white/10 rounded-full" />
            ) : isSnoozed ? (
            <Circle size={20} className="text-white/10" />
            ) : isPartiallyDone ? (
                <div className="relative w-5 h-5 flex items-center justify-center">
                    <Circle size={20} className="text-indigo-400" />
                    <span className="absolute text-[8px] font-bold text-indigo-200">{currentCount}</span>
                </div>
            ) : (
            <Circle size={20} />
            )}
        </button>
        
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
            {isExternal && (
                <span className="text-[10px] flex items-center gap-1 text-slate-400 bg-white/5 px-1.5 py-0.5 rounded border border-white/5">
                    <ExternalLink size={10} />
                    {t.linkedFrom}: {sourceBucketName}
                </span>
            )}
            
            {showBucketName && !isExternal && bucketName && (
                 <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 ${colors.text}`}>
                    {bucketName}
                 </span>
            )}

            <span className={`text-sm font-medium transition-all break-words leading-snug flex-1 ${
                todo.completed ? 'text-white/30 line-through' : 'text-white/90'
            }`}>
                {todo.text}
            </span>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
            
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 font-medium ${todo.completed ? 'bg-white/5 text-white/20' : 'bg-white/10 text-white/60'}`}>
                {todo.points}{t.pts}
            </span>

            {hasTargetCount && !todo.completed && !isSnoozed && (
                <span className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-indigo-500/20 bg-indigo-500/20 text-indigo-300 font-bold`} title={t.sessionsDone(currentCount, todo.targetCount || 1)}>
                    <ListTodo size={10} />
                    {currentCount} / {todo.targetCount}
                </span>
            )}

            {todo.scheduledDate && !todo.completed && (
                    <span className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-white/10 bg-amber-500/20 text-amber-300 border-amber-500/30 font-medium`} title={t.scheduledDate}>
                    <Calendar size={10} />
                    {getScheduledDateLabel(todo.scheduledDate)}
                </span>
            )}

            {(totalDisplayTime > 0 || isActive) && (
                <span className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono ${
                    isActive 
                        ? 'bg-indigo-500 text-white border-indigo-400 font-bold animate-pulse'
                        : 'bg-white/10 text-white/50 border-white/10'
                }`}>
                    {isActive ? <Hourglass size={8} className="animate-spin-slow" /> : <Clock size={8} />}
                    {timeString}
                </span>
            )}

            {todo.recurrence && (
                <span className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-white/10 ${
                    isSnoozed
                    ? 'bg-slate-800 text-slate-500 border-slate-700'
                    : todo.completed 
                        ? 'bg-white/5 text-white/20' 
                        : 'bg-indigo-500/30 text-indigo-200 border-indigo-500/40'
                }`}>
                    {isSnoozed 
                        ? <Clock size={10} /> 
                        : (todo.recurrence === 'daily' 
                            ? <CalendarClock size={10} /> 
                            : todo.recurrence === 'weekly' 
                                ? <CalendarDays size={10} /> 
                                : <Calendar size={10} />
                            )
                    }
                    {isSnoozed && todo.snoozedUntil
                        ? getSnoozeLabel(todo.snoozedUntil)
                        : getRecurrenceLabel(todo.recurrence)
                    }
                </span>
            )}
            </div>
        </div>

        {!todo.completed && !isSnoozed && !isClosed && (
            <div className="flex items-center gap-1 self-center">
                <button
                    onClick={(e) => { e.stopPropagation(); onToggleTimer(); }}
                    title={isActive ? t.stopTimer : t.startTimer}
                    className={`p-1.5 rounded-lg transition-all ${
                        isActive 
                            ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/50 scale-105' 
                            : 'text-white/30 hover:text-indigo-400 hover:bg-white/10'
                    }`}
                >
                    {isActive ? <Pause size={14} className="fill-current" /> : <Play size={14} className="fill-current" />}
                </button>
            </div>
        )}
        
        {!isClosed && !isExternal && (
            <div className="flex items-center gap-1 self-center opacity-0 group-hover/item:opacity-100 transition-opacity">
                <button 
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    className="p-1.5 text-white/30 hover:text-white/80 transition-colors"
                >
                    <Pencil size={14} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="p-1.5 text-white/30 hover:text-red-400 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>
        )}
        </div>
    );
}